import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swiper from 'swiper'
import 'swiper/css'
import Preview from '@renderer/components/Preview'
import { ProcessingModal } from '@renderer/components'
import { Countdown } from '@renderer/utils/countdownUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { getNextPageAfterFilter } from '@renderer/utils/navigationUtils'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { applyLUTThenOverlay, fetchLUTData, applyFilterOverlayImage, type FilterOverlayImage} from '@renderer/utils/lutUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import type { FilterAdjustments, PostLUTAdjustments } from '@renderer/types/configState'

/**
 * Renders a captured still with a filter baked in, for the post-capture
 * (`applyAfterCapture`) flow where there is no live camera feed to preview against.
 */
const FilteredImagePreview: React.FC<{
  imageUrl: string
  filterAdjustments?: FilterAdjustments
  filterMode?: 'manual' | 'lut'
  filterId?: string
  postLUTAdjustments?: PostLUTAdjustments
  /** The filter's overlay layer; applies in both Manual and LUT mode. */
  filterOverlay?: FilterOverlayImage | null
}> = ({
  imageUrl,
  filterAdjustments,
  filterMode,
  filterId,
  postLUTAdjustments,
  filterOverlay
}) => {
  const [filteredImageUrl, setFilteredImageUrl] = useState<string>(imageUrl)
  const imgRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const img = imgRef.current
    const canvas = canvasRef.current
    if (!img || !canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    const render = (): void => {
      canvas.width = img.naturalWidth || img.width
      canvas.height = img.naturalHeight || img.height
      if (!canvas.width || !canvas.height) return
      ctx.drawImage(img, 0, 0)

      if (filterMode === 'lut' && filterId) {
        applyLUTThenOverlay(ctx, { _id: filterId, filterMode: 'lut', postLUTAdjustments })
      } else if (filterAdjustments) {
        filterUtils.applyAdjustments(ctx, filterAdjustments)
      }
      applyFilterOverlayImage(ctx, filterOverlay)

      setFilteredImageUrl(canvas.toDataURL())
    }

    if (img.complete) {
      render()
    } else {
      img.onload = render
      img.onerror = (): void => setFilteredImageUrl(imageUrl)
    }

    return (): void => {
      img.onload = null
      img.onerror = null
    }
  }, [imageUrl, filterAdjustments, filterMode, filterId, postLUTAdjustments, filterOverlay])

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <img ref={imgRef} src={imageUrl} alt="" className="hidden" crossOrigin="anonymous" />
      <canvas ref={canvasRef} className="hidden" />
      <img
        src={filteredImageUrl}
        alt="Filtered photo"
        className="w-full h-full object-cover"
        crossOrigin="anonymous"
      />
    </div>
  )
}

const FilterPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeIndex, setActiveIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const swiperRef = useRef<Swiper | null>(null)
  const { filters, getPageSettings, config } = useConfigStore()
  const { setSessionState, gallery } = useSessionStateStore()
  const sessionStateStore = useSessionStateStore
  const capturePreview = unifiedCameraUtils.capturePreview
  const filterTimeout = getPageSettings('filter')?.filterTimeout || 0
  const countdownInstance = useRef<Countdown | null>(null)
  const { projectId } = useParams()

  // applyAfterCapture: the Filter page runs AFTER capture, so previews come from the
  // captured stills (there is no live feed to filter) and the choice must be baked
  // into the already-taken photos before Select renders them.
  const applyAfterCapture = config?.pages.filter?.settings?.applyAfterCapture || false
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // The countdown below is created once on mount, so calling it directly would fire the
  // mount-time closure and submit whatever was selected then (index 0) instead of the
  // guest's current choice. Both refs are kept current on every render.
  const continueRef = useRef<() => void>(() => {})
  const activeIndexRef = useRef(0)
  activeIndexRef.current = activeIndex

  useEffect(() => {
    if (!applyAfterCapture) {
      setCapturedImageUrl(null)
      return
    }
    const firstPhoto = gallery.capturedItems.find(
      (item) =>
        item.photoUrl &&
        !item.id?.includes('dreambooth-strip') &&
        !item.id?.includes('dreambooth-gif') &&
        !item.id?.includes('dreambooth-animation')
    )
    setCapturedImageUrl(firstPhoto?.photoUrl || null)
  }, [applyAfterCapture, gallery.capturedItems])

  useEffect((): (() => void) => {
    swiperRef.current = new Swiper('.mainSwiper', {
      loop: false,
      slidesPerView: 3,
      centeredSlides: true,
      spaceBetween: 10,
      direction: 'horizontal'
    })

    swiperRef.current.on('realIndexChange', (swiper) => {
      setActiveIndex(swiper.realIndex)
    })

    if (filters.length > 0) {
      swiperRef.current.slideToLoop(1)
    }

    if (filterTimeout > 0) {
      countdownInstance.current = new Countdown(
        filterTimeout,
        () => setRemainingTime(countdownInstance.current!.getFormattedRemainingSeconds()),
        () => {
          setSessionState({
            timeline: {
              activityLogs: [
                {
                  page: 'filter',
                  type: 'selection',
                  action: 'timeout',
                  details: {
                    filterId: filters[activeIndexRef.current]?._id,
                    timeoutDuration: filterTimeout
                  },
                  timestamp: new Date()
                }
              ]
            }
          })
          continueRef.current()
        }
      )
      countdownInstance.current.start()
    }

    return () => {
      if (swiperRef.current) swiperRef.current.destroy()
      countdownInstance.current?.stop()
    }
  }, [])

  /**
   * Bakes the chosen filter into every captured still and repoints the gallery at the
   * filtered versions, so Select/Result/print all show what the guest picked. Bounded
   * per photo with an overall deadline: a stalled fetch costs one timeout rather than
   * freezing the modal, and Select falls back to the original for anything unfinished.
   */
  const applyFilterToCapturedPhotos = async (filterId: string): Promise<void> => {
    const photosToProcess = sessionStateStore
      .getState()
      .gallery.capturedItems.filter(
        (item) =>
          item.photoUrl &&
          item.id &&
          !item.id.includes('dreambooth-strip') &&
          !item.id.includes('dreambooth-gif') &&
          !item.id.includes('dreambooth-animation')
      )
    if (!photosToProcess.length) return

    // applyLUTThenOverlay reads the LUT cache synchronously and silently no-ops on a
    // miss (producing unfiltered photos), so re-fetch if the config precache failed.
    if (filters.find((f) => f._id === filterId)?.filterMode === 'lut') {
      await fetchLUTData(filterId).catch(() => null)
    }

    const perPhotoTimeoutMs = 12000
    const overallDeadlineAt = Date.now() + Math.max(20000, photosToProcess.length * 4000)
    for (const item of photosToProcess) {
      if (Date.now() >= overallDeadlineAt) break
      await backgroundFilterProcessor.processPhotoNow(
        item.originalPhotoUrl || item.photoUrl!,
        item.id!,
        filterId,
        perPhotoTimeoutMs
      )
    }

    const { gallery: latestGallery } = sessionStateStore.getState()
    setSessionState({
      gallery: {
        ...latestGallery,
        capturedItems: latestGallery.capturedItems.map((item) =>
          item.photoUrl && item.filteredVersions?.[filterId]
            ? { ...item, photoUrl: item.filteredVersions[filterId] }
            : item
        )
      }
    })
  }

  const continueToPhotoSession = async (): Promise<void> => {
    const selectedFilter = filters[activeIndex]
    if (!selectedFilter || isProcessing) return

    setSessionState({
      userPreferences: {
        filterId: selectedFilter._id
      },
      timeline: {
        activityLogs: [
          {
            page: 'filter',
            type: 'selection',
            action: 'complete',
            details: { filterId: selectedFilter._id },
            timestamp: new Date()
          }
        ]
      }
    })

    if (applyAfterCapture) {
      setIsProcessing(true)
      try {
        await applyFilterToCapturedPhotos(selectedFilter._id)
      } catch (error) {
        // Never trap the guest on Filter: Select falls back to the originals.
        console.error('[PHOTOPHORIA-FILTER] Post-capture filter processing failed', error)
      }
      setIsProcessing(false)
    }

    navigate(`/${projectId}/${getNextPageAfterFilter(config)}`)
  }

  continueRef.current = continueToPhotoSession

  const slidePrev = (): void => {
    swiperRef.current?.slidePrev()
  }

  const slideNext = (): void => {
    swiperRef.current?.slideNext()
  }

  const handleClick = (index: number): void => {
    swiperRef.current?.slideToLoop(index)
  }

  // Call all hooks at the top level
  const cachedFilterBg = useCachedBackgroundImage('/assets/photophoria/photophoria-filter.webp')
  const cachedFilterPrev = useCachedImage('/assets/photophoria/photophoria-filter-prev.webp')
  const cachedFilterNext = useCachedImage('/assets/photophoria/photophoria-filter-next.webp')
  const cachedFilterOption1 = useCachedImage('/assets/photophoria/photophoria-filter-option-1.webp')
  const cachedFilterOption2 = useCachedImage('/assets/photophoria/photophoria-filter-option-2.webp')
  const cachedFilterOption3 = useCachedImage('/assets/photophoria/photophoria-filter-option-3.webp')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{
        backgroundImage: `url(${cachedFilterBg})`
      }}
    >
      <div className="absolute top-[150px] left-1/2 -translate-x-1/2 w-3/4 h-[700px] flex justify-between items-center">
        <button
          onClick={slidePrev}
          className="mx-8 hover:opacity-90 transition-opacity"
          aria-label="Previous filter"
        >
          <img src={cachedFilterPrev} alt="Previous" className="w-24 h-auto" />
        </button>
        <div className="swiper mainSwiper w-full flex items-center justify-center">
          <div className="swiper-wrapper cursor-grab p-0 active:cursor-grabbing focus:cursor-grabbing w-full transition-all duration-300">
            {filters.map((filter, index) => (
              <div
                className="swiper-slide flex flex-col p-0 items-center justify-center transition-all h-[700px] duration-300"
                key={filter._id}
              >
                {/* Filter Card Container */}
                <div
                  onClick={() => handleClick(index)}
                  className={`relative w-[400px] h-[400px] flex flex-col items-center justify-center transition-all duration-300 cursor-pointer
                    ${activeIndex === index ? '' : 'mt-8 scale-90'}`}
                >
                  {/* Preview Component (behind frame) */}
                  <div className="absolute top-[50px] left-[25px] w-[350px] h-[320px] overflow-hidden rounded-lg z-0">
                    {applyAfterCapture && capturedImageUrl ? (
                      // Post-capture: the camera feed is gone, so preview the guest's own
                      // photo with each filter applied.
                      <FilteredImagePreview
                        imageUrl={capturedImageUrl}
                        filterAdjustments={filter.adjustments}
                        filterMode={filter.filterMode}
                        filterId={filter._id}
                        postLUTAdjustments={filter.postLUTAdjustments}
filterOverlay={filter.overlay}
                      />
                    ) : (
                      <Preview
                        capturePreview={capturePreview}
                        adjustments={filter.adjustments}
                        filter={filter._id}
                        ratio={1}
                        cameraRatio={1}
                        previewQuality={{
                          maxWidth: 350,
                          maxHeight: 350,
                          quality: 0.8
                        }}
                      />
                    )}
                  </div>
                  {/* Frame Image (in front of preview) */}
                  <img
                    src={
                      (index % 3) + 1 === 1
                        ? cachedFilterOption1
                        : (index % 3) + 1 === 2
                          ? cachedFilterOption2
                          : cachedFilterOption3
                    }
                    alt={`Filter Frame ${(index % 3) + 1}`}
                    className="absolute inset-0 w-full h-full object-contain z-10"
                  />
                  {/* Filter Name (optional, position as needed) */}
                  {/* <div className="text-center absolute bottom-[-40px] left-1/2 -translate-x-1/2 z-20">
                    <h2
                      className={`text-carnabyTan font-deco-samu transition-all duration-300 
                      ${activeIndex === index ? 'text-4xl' : 'text-3xl'}`}
                    >
                      {filter.name}
                    </h2>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={slideNext}
          className="mx-8 hover:opacity-90 transition-opacity"
          aria-label="Next filter"
        >
          <img src={cachedFilterNext} alt="Next" className="w-24 h-auto" />
        </button>
      </div>
      <div className="absolute bottom-[30px] right-[50px] flex flex-col items-center">
        <button
          onClick={continueToPhotoSession}
          disabled={isProcessing}
          className="transition-transform transform hover:scale-105 disabled:opacity-50"
        >
          <img src={cachedFilterNext} alt="Continue" className="w-32 h-auto" />
        </button>
        <span className="text-gray-500 text-lg font-serif-bahela">{remainingTime}</span>
      </div>
      <ProcessingModal
        isVisible={isProcessing}
        title="Applying filter..."
        message="Please wait while we apply your filter to your photos"
      />
    </div>
  )
}

export default FilterPage
